import { useState } from 'react';
import { PlusCircle, Truck, MapPin, Bell, Check, ChevronLeft } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { useToast } from './Toast';
import { useCompleteOnboarding } from '../../features/users/hooks';

type StepName = 'Booking' | 'Tracking' | 'Locations' | 'Notifications';

const STEPS: StepName[] = ['Booking', 'Tracking', 'Locations', 'Notifications'];

const STEP_CONTENT: Record<StepName, { icon: typeof PlusCircle; heading: string; body: string }> = {
  Booking: {
    icon: PlusCircle,
    heading: 'Book equipment in minutes',
    body: 'Request equipment transport in a few steps: describe what’s moving, set pickup and drop-off, and we’ll follow up to confirm the details.',
  },
  Tracking: {
    icon: Truck,
    heading: 'Track your job live',
    body: 'Once a driver is assigned, watch their location update in real time, no more guessing when your equipment will arrive.',
  },
  Locations: {
    icon: MapPin,
    heading: 'Save your regular sites',
    body: 'Add job sites or depots you use often so future bookings take seconds, not retyping addresses.',
  },
  Notifications: {
    icon: Bell,
    heading: 'Stay in the loop',
    body: 'Get notified the moment your request is quoted, a driver is assigned, or your equipment is delivered.',
  },
};

type Props = {
  open: boolean;
};

export function OnboardingModal({ open }: Props) {
  const [step, setStep] = useState(0);
  const completeOnboarding = useCompleteOnboarding();
  const { toast } = useToast();

  const stepName = STEPS[step];
  const isLastStep = step === STEPS.length - 1;
  const { icon: Icon, heading, body } = STEP_CONTENT[stepName];

  const next = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const finish = () => {
    if (completeOnboarding.isPending) return;
    completeOnboarding.mutate(undefined, {
      onSuccess: () => setStep(0),
      onError: () => toast("Couldn't save that, please try again.", 'error'),
    });
  };

  return (
    <Modal open={open} onClose={finish} title="Welcome to RT Equipment & Haulage" maxWidth="max-w-lg" hideCloseButton>
      <div className="flex flex-col gap-5">
        <div className="flex divide-x divide-line border-b border-line">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`flex-1 py-2 text-center text-[10px] font-bold uppercase tracking-widest transition-colors ${
                i < step ? 'bg-accent/10 text-accent' : i === step ? 'bg-accent text-black' : 'text-muted'
              }`}
            >
              {i < step ? <Check size={11} className="mx-auto" /> : i + 1}
            </div>
          ))}
        </div>

        <div key={stepName} className="step-enter flex flex-col items-center gap-3 py-4 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/10">
            <Icon size={26} className="text-accent" />
          </div>
          <h3 className="text-base font-black text-text">{heading}</h3>
          <p className="text-sm text-muted leading-relaxed max-w-sm">{body}</p>
        </div>

        <div className="flex justify-between gap-2">
          {!isLastStep && (
            <Button
              variant="ghost"
              size="md"
              onClick={finish}
              loading={completeOnboarding.isPending}
              loadingText="Skipping…"
              className="!text-muted text-sm !tracking-normal rounded-xl"
            >
              Skip tour
            </Button>
          )}
          <div className="flex gap-2 ml-auto">
            {step > 0 && (
              <Button
                variant="secondary"
                size="md"
                onClick={prev}
                disabled={completeOnboarding.isPending}
                className="text-sm !tracking-normal rounded-xl"
              >
                <ChevronLeft size={15} />
                Back
              </Button>
            )}
            <Button
              variant="primary"
              size="md"
              onClick={isLastStep ? finish : next}
              loading={isLastStep && completeOnboarding.isPending}
              loadingText="Finishing…"
              className="text-sm !tracking-normal rounded-xl"
            >
              {isLastStep ? 'Get Started' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
